/**
 * Typography-neutral word-level diff (spec §5.2 "[1.1] Typography").
 *
 * Both sides are split into tokens (runs of letters/digits/marks, runs of whitespace, single punctuation
 * characters). Tokens are COMPARED by their normalised key (`normalizeForMatch`: curly vs straight quotes,
 * dashes, NBSP and other spaces, whitespace runs, zero-width/soft-hyphen characters all compare equal) but
 * EMITTED with their original characters. So an unchanged token never becomes an edit just because the model
 * typed `'` where the document has `’`, and the document's own characters are kept in every equal region.
 *
 * Changed regions keep the replacement's characters as written.
 */

import { IGNORABLE, WHITESPACE, normalizeChars, normalizeForMatch } from "./normalize";

type TokenClass = "word" | "space" | "punct";

export interface Token {
  /** Original characters. */
  text: string;
  /** Comparison key (normalised). */
  key: string;
  /** UTF-16 span in the source string. */
  start: number;
  end: number;
}

const WORD_CHAR = /[\p{L}\p{N}\p{M}_]/u;

function classify(normalizedChar: string): TokenClass | "ignorable" {
  if (IGNORABLE.has(normalizedChar)) return "ignorable";
  if (WHITESPACE.has(normalizedChar)) return "space";
  return WORD_CHAR.test(normalizedChar) ? "word" : "punct";
}

/** Split `text` into tokens covering it completely and in order. */
export function tokenize(text: string): Token[] {
  const normalized = normalizeChars(text); // same length, same indices
  const tokens: Token[] = [];
  let current: { cls: TokenClass; start: number; end: number } | null = null;
  let pendingStart: number | null = null; // leading ignorables before the first token
  const flush = (): void => {
    if (current === null) return;
    const raw = text.slice(current.start, current.end);
    const key = current.cls === "space" ? " " : normalizeForMatch(raw).text;
    tokens.push({ text: raw, key, start: current.start, end: current.end });
    current = null;
  };
  let i = 0;
  for (const ch of normalized) {
    const start = i;
    const end = i + ch.length;
    i = end;
    const cls = classify(ch);
    if (cls === "ignorable") {
      if (current !== null) current.end = end;
      else if (pendingStart === null) pendingStart = start;
      continue;
    }
    const tokenStart: number = pendingStart ?? start;
    pendingStart = null;
    const cur = current as { cls: TokenClass; start: number; end: number } | null;
    if (cur !== null && cur.cls === cls && cls !== "punct") {
      cur.end = end;
      continue;
    }
    flush();
    current = { cls, start: tokenStart, end };
  }
  if (current === null && pendingStart !== null) {
    // Only ignorable characters: one token whose key is empty.
    tokens.push({ text: text.slice(pendingStart), key: "", start: pendingStart, end: text.length });
  }
  flush();
  return tokens;
}

/** One changed region, in UTF-16 offsets of the OLD text. `oldFrom === oldTo` means a pure insertion. */
export interface TextHunk {
  oldFrom: number;
  oldTo: number;
  oldText: string;
  newText: string;
}

export type Segment = { kind: "equal" | "delete" | "insert"; text: string };

export interface WordDiff {
  hunks: TextHunk[];
  /** For display: equal text is the OLD (document) text. */
  segments: Segment[];
}

/** Above this many DP cells the caller falls back to replacing the whole span. */
export const MAX_DIFF_CELLS = 4_000_000;

type Op = { kind: "equal"; i: number; j: number } | { kind: "delete"; i: number } | { kind: "insert"; j: number };

function lcsOps(a: readonly string[], b: readonly string[]): Op[] | null {
  // Trim the common prefix and suffix first; most edits touch a few tokens of a long clause.
  let pre = 0;
  while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++;
  let suf = 0;
  while (suf < a.length - pre && suf < b.length - pre && a[a.length - 1 - suf] === b[b.length - 1 - suf]) suf++;
  const n = a.length - pre - suf;
  const m = b.length - pre - suf;
  if ((n + 1) * (m + 1) > MAX_DIFF_CELLS) return null;
  const width = m + 1;
  const table = new Uint32Array((n + 1) * (m + 1));
  for (let x = n - 1; x >= 0; x--) {
    for (let y = m - 1; y >= 0; y--) {
      table[x * width + y] =
        a[pre + x] === b[pre + y]
          ? (table[(x + 1) * width + y + 1] as number) + 1
          : Math.max(table[(x + 1) * width + y] as number, table[x * width + y + 1] as number);
    }
  }
  const ops: Op[] = [];
  for (let k = 0; k < pre; k++) ops.push({ kind: "equal", i: k, j: k });
  let x = 0;
  let y = 0;
  while (x < n && y < m) {
    if (a[pre + x] === b[pre + y]) {
      ops.push({ kind: "equal", i: pre + x, j: pre + y });
      x++;
      y++;
    } else if ((table[(x + 1) * width + y] as number) >= (table[x * width + y + 1] as number)) {
      ops.push({ kind: "delete", i: pre + x });
      x++;
    } else {
      ops.push({ kind: "insert", j: pre + y });
      y++;
    }
  }
  for (; x < n; x++) ops.push({ kind: "delete", i: pre + x });
  for (; y < m; y++) ops.push({ kind: "insert", j: pre + y });
  for (let k = 0; k < suf; k++) ops.push({ kind: "equal", i: a.length - suf + k, j: b.length - suf + k });
  return ops;
}

/** Typography-neutral word diff of `oldText` (the document's characters) against `newText`. */
export function wordDiff(oldText: string, newText: string): WordDiff | null {
  const oldTokens = tokenize(oldText);
  const newTokens = tokenize(newText);
  const ops = lcsOps(
    oldTokens.map((t) => t.key),
    newTokens.map((t) => t.key),
  );
  if (ops === null) return null;
  const hunks: TextHunk[] = [];
  const segments: Segment[] = [];
  const pushSegment = (kind: Segment["kind"], text: string): void => {
    if (!text) return;
    const last = segments[segments.length - 1];
    if (last !== undefined && last.kind === kind) last.text += text;
    else segments.push({ kind, text });
  };
  let k = 0;
  while (k < ops.length) {
    const op = ops[k] as Op;
    if (op.kind === "equal") {
      pushSegment("equal", (oldTokens[op.i] as Token).text);
      k++;
      continue;
    }
    // A maximal run of deletes/inserts is one hunk.
    const deleted: Token[] = [];
    const inserted: Token[] = [];
    let insertionPoint: number | null = null;
    while (k < ops.length && (ops[k] as Op).kind !== "equal") {
      const cur = ops[k] as Op;
      if (cur.kind === "delete") deleted.push(oldTokens[cur.i] as Token);
      else if (cur.kind === "insert") inserted.push(newTokens[cur.j] as Token);
      k++;
    }
    if (deleted.length === 0) {
      const next = ops[k];
      insertionPoint =
        next !== undefined && next.kind === "equal"
          ? (oldTokens[next.i] as Token).start
          : oldText.length;
    }
    const oldFrom = deleted.length > 0 ? (deleted[0] as Token).start : (insertionPoint as number);
    const oldTo = deleted.length > 0 ? (deleted[deleted.length - 1] as Token).end : oldFrom;
    const newPart =
      inserted.length > 0
        ? newText.slice((inserted[0] as Token).start, (inserted[inserted.length - 1] as Token).end)
        : "";
    const oldPart = oldText.slice(oldFrom, oldTo);
    // Deleted/inserted tokens whose keys are empty (only ignorable characters) are not real edits.
    if (normalizeForMatch(oldPart).text === normalizeForMatch(newPart).text) {
      pushSegment("equal", oldPart);
      continue;
    }
    hunks.push({ oldFrom, oldTo, oldText: oldPart, newText: newPart });
    pushSegment("delete", oldPart);
    pushSegment("insert", newPart);
  }
  return { hunks, segments };
}

/**
 * Index of the occurrence of `needle` that starts at `position`, counting non-overlapping occurrences from
 * the left (how Word's `search()` reports them). -1 when `position` is not such an occurrence.
 */
export function occurrenceIndex(haystack: string, needle: string, position: number): number {
  if (!needle) return -1;
  let k = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1 && index <= position) {
    if (index === position) return k;
    k++;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return -1;
}

/** Number of non-overlapping occurrences of `needle` in `haystack`. */
export function occurrenceCount(haystack: string, needle: string): number {
  if (!needle) return 0;
  let k = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    k++;
    index = haystack.indexOf(needle, index + needle.length);
  }
  return k;
}
